import _ from 'lodash';
import { mapGetters } from 'vuex';
import {
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUser,
} from 'amazon-cognito-identity-js';

const cognitoMixins = {
  computed: {
    ...mapGetters(['stormPool']),
  },
  methods: {
    guid() {
      function s4() {
        return Math.floor((1 + Math.random()) * 0x10000)
          .toString(16)
          .substring(1);
      }
      return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
    },
    async stormInvoke(params) {
      await this.checkStormSession();
      const lambda = new this.$aws.Lambda({ region: process.env.LAMBDA_REGION });
      const call = await lambda.invoke({
        FunctionName: `${process.env.STORM_PROXY}:${process.env.STORM_PROXY_VERSION}`,
        InvocationType: 'RequestResponse',
        Payload: JSON.stringify(params.data),
        LogType: 'None',
      }).promise();
      const response = this._extractResponse(call.Payload);
      const data = this._extractResponse(response.body);
      return { data };
    },
    // Cognito mixins
    // https://github.com/aws-amplify/amplify-js/tree/master/packages/amazon-cognito-identity-js
    cognitoConfirmNewUser(p) {
      const userData = {
        Username: p.email,
        Pool: this.stormPool,
      };

      const cognitoUser = new CognitoUser(userData);

      return new Promise((resolve, reject) => {
        cognitoUser.confirmRegistration(p.code, true, (err) => {
          if (err) {
            reject(err);
            return;
          }
          // if we want to authenticate user right after confirmation and
          // give them a session
          const authData = {
            Username: p.email,
            Password: p.password,
          };
          const authDetails = new AuthenticationDetails(authData);
          cognitoUser.authenticateUser(authDetails, {
            onSuccess(result) {
              resolve(result);
            },
            onFailure(error) {
              reject(error);
            },
          });
        });
      });
    },
    cognitoValidateSession() {
      return new Promise(async (resolve) => {
        const session = await this.cognitoRestoreSession();

        if (session) {
          // if there is a session, set aws creds in state so this user can have
          // it's limited permissions
          const Creds = this.$aws.CognitoIdentityCredentials;
          const cogUrl = `cognito-idp.us-west-2.amazonaws.com/${process.env.STORM_USER_POOL_ID}`;
          const params = {
            IdentityPoolId: process.env.STORM_FEDERATED_POOL_ID,
            Logins: {
              // only allow cognito web users
              [cogUrl]: session.getIdToken().getJwtToken(),
            },
          };
          this.$aws.config.credentials = new Creds(params, { region: process.env.STORM_REGION });
          this.$log.info('Storm session set for: ', session.idToken.payload.email);
          await this.$aws.config.credentials.getPromise();
          this.$log.info('Storm $aws credentials: ', this.$aws.config.credentials);
          resolve(session);
        } else {
          // route to login if there is no user session
          this.$router.push({ path: '/storm-login' });
          resolve(false);
        }
      });
    },
    cognitoRestoreSession() {
      const cognitoUser = this.stormPool.getCurrentUser();
      return new Promise((resolve, reject) => {
        if (cognitoUser !== null) {
          // there is a saved session
          cognitoUser.getSession(async (err, session) => {
            if (err) {
              // user was deleted and trying to reload so move them
              // to login
              if (['NotAuthorizedException', 'UserNotFoundException'].includes(err.code)) {
                this.$router.push({ path: '/storm-login' });
              }
              reject(err);
            }
            if (session && session.isValid()) {
              resolve(session);
            } else {
              resolve(false);
            }
          });
        } else {
          // there is no session
          resolve(false);
        }
      });
    },
    getCognitoStormUser() {
      const cognitoUser = this.stormPool.getCurrentUser();
      return new Promise((resolve, reject) => {
        if (cognitoUser !== null) {
          cognitoUser.getSession(async (sessionError, session) => {
            if (sessionError) {
              reject(sessionError);
            }
            if (session && session.isValid()) {
              resolve(cognitoUser);
            }
          });
        } else {
          resolve(false);
        }
      });
    },
    clearCognitoKeys() {
      _.forOwn(window.localStorage, (value, key) => {
        if (key.match(/^CognitoIdentityServiceProvider/gi) || key.match(/^aws.cognito.identity/gi)) {
          window.localStorage.removeItem(key);
        }
      });
    },
    async checkStormSession() {
      const $creds = this.$aws.config.credentials;
      if ($creds && $creds.needsRefresh()) {
        this.$log.warn('storm session needs refresh');
        const session = await this.cognitoValidateSession();
        this.$store.dispatch('setStormSession', { session });
      }
    },
    async cognitoGetUserAttrs() {
      const cognitoUser = await this.getCognitoStormUser();
      return new Promise((resolve, reject) => {
        cognitoUser.getUserAttributes((err, result) => {
          if (err) {
            reject(err.message || JSON.stringify(err));
            return;
          }
          resolve(result);
        });
      });
    },
    cognitoLogin(p) {
      const authData = {
        Username: p.username,
        Password: p.password,
      };

      const authDetails = new AuthenticationDetails(authData);

      const userData = {
        Username: p.username,
        Pool: this.stormPool,
      };
      const cognitoUser = new CognitoUser(userData);
      return new Promise((resolve, reject) => {
        cognitoUser.authenticateUser(authDetails, {
          onSuccess(result) {
            resolve(result);
          },
          onFailure(err) {
            reject(err);
          },
          newPasswordRequired(userAttributes) {
            // uneeded 2nd param requiredAttributes
            userAttributes.name = 'Storm User';
            // User was signed up by an admin and must provide new
            // password and required attributes, if any, to complete
            // authentication.

            // the api doesn't accept this field back
            // delete userAttributes.email_verified;
            //
            // // unsure about this field, but I don't send this back
            // delete userAttributes.phone_number_verified;
            //
            // // Get these details and call
            cognitoUser.completeNewPasswordChallenge(p.password, userAttributes, {
              onSuccess(result) {
                resolve(result);
              },
              onFailure(err) {
                reject(err);
              },
            });
          },
        });
      });
    },
    cognitoLogout() {
      const cognitoUser = this.stormPool.getCurrentUser();
      return new Promise((resolve, reject) => {
        if (cognitoUser) {
          resolve(cognitoUser.signOut());
        } else {
          reject('logout failed!');
        }
      });
    },
    cognitoChangePassword(p) {
      const cognitoUser = this.stormPool.getCurrentUser();
      return new Promise((resolve, reject) => {
        if (cognitoUser !== null) {
          // there is a saved session
          cognitoUser.getSession(async (err, session) => {
            if (err) {
              reject(err);
            }
            if (session.isValid()) {
              cognitoUser.changePassword(p.oldPassword, p.newPassword, (err, result) => {
                if (err) {
                  reject(err);
                }
                resolve(result);
              });
            }
          });
        } else {
          // there is no session
          reject(false);
        }
      });
    },
    cognitoUpdateAttrs(p) {
      const cognitoUser = this.stormPool.getCurrentUser();

      const attrs = [];
      p.attrs.forEach((attr) => {
        attrs.push(new CognitoUserAttribute(attr));
      });

      return new Promise((resolve, reject) => {
        if (cognitoUser !== null) {
          // there is a saved session
          cognitoUser.getSession(async (err, session) => {
            if (err) {
              reject(err);
            }
            if (session.isValid()) {
              cognitoUser.updateAttributes(attrs, (err, result) => {
                if (err) {
                  reject(err);
                  return;
                }
                resolve(result);
              });
            }
          });
        } else {
          reject(false);
        }
      });
    },
    cognitoForgotPassword(p) {
      const userData = {
        Username: p.email,
        Pool: this.stormPool,
      };

      const cognitoUser = new CognitoUser(userData);

      return new Promise((resolve, reject) => {
        cognitoUser.forgotPassword({
          onSuccess() {
            resolve();
          },
          onFailure(err) {
            reject(err);
          },
        });
      });
    },
    cognitoSubmitResetPass(p) {
      const userData = {
        Username: p.email,
        Pool: this.stormPool,
      };

      const cognitoUser = new CognitoUser(userData);

      return new Promise((resolve, reject) => {
        cognitoUser.confirmPassword(p.code, p.password, {
          onSuccess() {
            resolve(cognitoUser);
          },
          onFailure(err) {
            reject(err);
          },
        });
      });
    },
    cognitoResendNewUserCode(p) {
      const userData = {
        Username: p.email,
        Pool: this.stormPool,
      };
      const cognitoUser = new CognitoUser(userData);
      return new Promise((resolve, reject) => {
        cognitoUser.resendConfirmationCode((err, result) => {
          if (err) {
            reject(err);
            return;
          }
          resolve(result);
        });
      });
    },
    forceDrzzleLogout() {
      const cognitoUser = this.userPool.getCurrentUser();
      return new Promise((resolve, reject) => {
        if (cognitoUser) {
          resolve(cognitoUser.signOut());
        } else {
          reject('No Drzzle user to log out!');
        }
      });
    },
  },
};

export { cognitoMixins }; // eslint-disable-line



// WEBPACK FOOTER //
// ./src/storm/mixins/cognito.js